import {ProgressCircle} from '@github-ui/progress-circle'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useSubIssuesSummary} from '@github-ui/sub-issues/useSubIssuesSummary'
import {Token} from '@primer/react'

import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import styles from './HeaderSubIssuesSummary.module.css'

// RELAY-CLEANUP(HeaderMetadata, StickyHeaderChips): widens nullable fields so the still-Relay
// parents' data is assignable. Remove when HeaderMetadata and StickyHeaderChips leave Relay.
type HeaderSubIssueSummaryWithPrimaryFragment = RelayCompat<Pick<IssueSecondaryNode, 'subIssuesSummary'>>

// RELAY-CLEANUP(IssueViewerSecondaryViewQuery): widens nullable fields so the still-Relay
// parent data is assignable. Remove when IssueViewerSecondaryViewQuery leaves Relay.
export type HeaderSubIssueSummaryFragment = RelayCompat<Pick<IssueSecondaryNode, 'subIssuesConnection' | 'subIssues'>>

export type HeaderSubIssueSummaryProps = {
  subIssuePrimaryKey?: HeaderSubIssueSummaryWithPrimaryFragment
  subIssueSecondaryKey?: HeaderSubIssueSummaryFragment
  size?: 'xlarge' | 'small'
}

export function HeaderSubIssueSummary({
  subIssuePrimaryKey,
  subIssueSecondaryKey,
  size = 'xlarge',
}: HeaderSubIssueSummaryProps) {
  // When the secondary query becomes available, prefer calculating summary with sub-issues directly, rather than using the summary
  // This avoids issues where the summary becomes stale after a live update
  const {completed, percentCompleted, total} = useSubIssuesSummary(subIssueSecondaryKey ?? undefined)

  return subIssueSecondaryKey ? (
    <HeaderSubIssueSummaryInternal
      completed={completed}
      percentCompleted={percentCompleted}
      total={total}
      size={size}
    />
  ) : (
    <HeaderSubIssueSummaryWithPrimary subIssueSummaryKey={subIssuePrimaryKey} size={size} />
  )
}

export type HeaderSubIssueSummarWithPrimaryProps = {
  subIssueSummaryKey?: HeaderSubIssueSummaryWithPrimaryFragment
  size?: 'xlarge' | 'small'
}

function HeaderSubIssueSummaryWithPrimary({
  subIssueSummaryKey: node,
  size = 'xlarge',
}: HeaderSubIssueSummarWithPrimaryProps) {
  const {completed, percentCompleted, total} = useSubIssuesSummary(node ?? undefined)

  if (!node) return <></>

  return (
    <HeaderSubIssueSummaryInternal
      completed={completed}
      percentCompleted={percentCompleted}
      total={total}
      size={size}
    />
  )
}

export type HeaderSubIssueSummaryInternalProps = {
  completed: number
  percentCompleted: number
  total: number
  size: 'xlarge' | 'small'
}

function HeaderSubIssueSummaryInternal({completed, total, percentCompleted, size}: HeaderSubIssueSummaryInternalProps) {
  const visualLabel = `${completed} / ${total}`
  const label = `${completed} of ${total} ${total === 1 ? 'issue' : 'issues'} completed`

  if (total === 0 || isNaN(percentCompleted)) return null

  if (size === 'small') {
    return (
      <div className={styles.smallSummary}>
        <ProgressCircle percentCompleted={percentCompleted} />

        <span aria-hidden="true" className={styles.visualLabel}>
          {visualLabel}
        </span>
        <span className="sr-only">{label}</span>
      </div>
    )
  }

  return (
    <Token
      text={
        <>
          <span aria-hidden="true" className={styles.visualLabel}>
            {visualLabel}
          </span>
          <span className="sr-only">{label}</span>
        </>
      }
      leadingVisual={() => <ProgressCircle percentCompleted={percentCompleted} />}
      className={styles.token}
      size="xlarge"
    />
  )
}
